import React, { useRef } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import c from 'classnames';

import { Icon } from '@uncinc/uncinc-react-kitchen-sink';

import SearchAllContent from '../../SearchAllContent';
import ButtonWrapper from '../../ButtonWrapper';

import { createSearchUrl } from '../../../helpers/createSearchURL';
import { APP_OPTIONS, SEARCH_PATH } from '../../../config/constants';

import './style.scss';

interface SoloSearchProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsible?: boolean;
  toggleOverlay?: (onlyClose?: boolean) => void;
  menuButtonRef?: React.Ref<HTMLElement>;
  searchInputRef?: React.Ref<HTMLInputElement>;
}

const SoloSearch: React.FC<SoloSearchProps> = React.memo(({
  collapsible = false,
  toggleOverlay = () => {},
  menuButtonRef,
  searchInputRef,
  ...rest
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isSearchCollapsed = useRef<boolean>(true);

  const SoloSearchClassNames = c('SoloSearch', {
    'is-collapsible': collapsible,
    'is-collapsed': collapsible && isSearchCollapsed.current,
  });

  const toggleSearchBar = () => {
    if (searchInputRef && typeof searchInputRef !== 'function' && searchInputRef?.current !== document.activeElement) {
      searchInputRef.current?.focus();
    }
  };

  const onSearchSubmit = (query: string) => {
    const url = createSearchUrl({
      pathname: SEARCH_PATH,
      index: APP_OPTIONS.parameters.searchkitIndexAllContent,
      value: query,
    });

    try {
      history.push(url);
      if (collapsible) {
        toggleSearchBar();
      }
      if (!collapsible) {
        toggleOverlay();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div ref={wrapperRef} className={SoloSearchClassNames} {...rest}>
      {!collapsible && (
        <p className="SoloSearch__label body--large">
          {t('pages.search.solo_body')}
        </p>
      )}
      {collapsible && (
        <ButtonWrapper
          className="SoloSearch__collapse-button__open"
          onClick={() => toggleSearchBar()}
          inverted
          aria-label={t('components.solo_search.collapse_button_open_label')}
        >
          <Icon flair="outlined" name="search" />
        </ButtonWrapper>
      )}
      <SearchAllContent
        className="SoloSearch__search"
        onlyFetchWithQuery
      >
        <SearchAllContent.SearchBar ref={searchInputRef} onSubmit={onSearchSubmit} />
        {collapsible && (
          <ButtonWrapper
            className="SoloSearch__collapse-button__close"
            onClick={() => menuButtonRef && typeof menuButtonRef !== 'function' && menuButtonRef.current?.focus()}
            inverted
            aria-label={t('components.solo_search.collapse_button_close_label')}
          >
            <Icon flair="outlined" name="close" />
          </ButtonWrapper>
        )}
      </SearchAllContent>
    </div>
  );
});

export default SoloSearch;
